import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { adminIcons, actionIcons, getIconSize } from '../../utils/iconMap';
import { motion, AnimatePresence } from 'framer-motion';
import AdminTopBar from './AdminTopBar';

const CloseIcon = actionIcons.x;
const MenuIcon = adminIcons.menu;
const HomeIcon = adminIcons.home;
const LogOutIcon = actionIcons.logOut;
const ArrowLeftIcon = actionIcons.arrowLeft;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdminHome = location.pathname === '/admin';

  const menuItems = [
    { icon: adminIcons.layoutDashboard, label: 'Dashboard', path: '/admin', testId: 'admin-nav-dashboard' },
    { icon: adminIcons.users, label: 'Kullanıcılar', path: '/admin/users', testId: 'admin-nav-users' },
    { icon: adminIcons.userRound, label: 'Avatarlar', path: '/admin/avatars', testId: 'admin-nav-avatars' },
    { icon: adminIcons.target, label: 'Görevler', path: '/admin/missions', testId: 'admin-nav-missions' },
    { icon: adminIcons.slidersHorizontal, label: 'Progression', path: '/admin/progression', testId: 'admin-nav-progression' },
    { icon: adminIcons.folderOpen, label: 'Seriler', path: '/admin/series', testId: 'admin-nav-series' },
    { icon: adminIcons.users2, label: 'Karakterler', path: '/admin/characters', testId: 'admin-nav-characters' },
    { icon: adminIcons.layers, label: 'Kart şablonları', path: '/admin/card-templates', testId: 'admin-nav-card-templates' },
    { icon: adminIcons.keyRound, label: 'Aktivasyon Kodları', path: '/admin/activation-codes', testId: 'admin-nav-activation-codes' },
    { icon: adminIcons.package, label: 'Kutu Operasyonları', path: '/admin/boxes', testId: 'admin-nav-boxes' },
    { icon: adminIcons.fileText, label: 'Audit Log', path: '/admin/audit', testId: 'admin-nav-audit' },
    { icon: adminIcons.activity, label: 'Canlılık ve Feed', path: '/admin/activity-feed-settings', testId: 'admin-nav-activity-feed' },
    { icon: adminIcons.gauge, label: 'Sistem tanıları', path: '/admin/system-diagnostics', testId: 'admin-nav-diagnostics' },
    ...(user?.role === 'superadmin'
      ? [{ icon: adminIcons.settings, label: 'Ayarlar', path: '/admin/settings', testId: 'admin-nav-settings' }]
      : []),
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <>
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Cinzel, serif' }}>
          DEKOSTA
        </h1>
        <p className="text-sm text-gray-400 mt-1">Admin Panel</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setSidebarOpen(false);
              }}
              data-testid={item.testId}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                isActive
                  ? 'bg-violet-600 text-white'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon
                className={`${getIconSize('medium')} ${isActive ? 'text-white' : 'text-gray-400'}`}
              />
              <span className="font-medium">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/10 space-y-2">
        <Button
          onClick={() => {
            navigate('/dashboard');
            setSidebarOpen(false);
          }}
          variant="outline"
          className="w-full border-white/20 text-white hover:bg-white/10"
          data-testid="admin-back-to-app"
        >
          <HomeIcon className={`${getIconSize('small')} mr-2`} />
          Oyuncu görünümüne dön
        </Button>
        <div className="flex items-center gap-3 mb-1 px-2 pt-1">
          <div className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center">
            <span className="text-white font-bold">{user?.name?.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold text-sm truncate">{user?.name}</div>
            <div className="text-gray-400 text-xs truncate">{user?.email}</div>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full border-red-500/20 text-red-400 hover:bg-red-500/5 mt-2"
          data-testid="admin-logout-button"
        >
          <LogOutIcon className={`${getIconSize('small')} mr-2`} />
          Çıkış Yap
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 glass-effect border-r border-white/10">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className="fixed inset-0 bg-black/80 z-40 md:hidden"
            />
            
            {/* Sidebar */}
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-64 glass-effect border-r border-white/10 z-50 md:hidden flex flex-col"
            >
              <button
                onClick={() => setSidebarOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/5"
              >
                <CloseIcon className={`${getIconSize('medium')} text-white`} />
              </button>
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0">
        <AdminTopBar />
        {/* Mobile Header */}
        <div className="md:hidden glass-effect border-b border-white/10 p-4 flex items-center justify-between gap-2">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-white/5 shrink-0"
            data-testid="mobile-menu-button"
          >
            <MenuIcon className="w-6 h-6 text-white" />
          </button>
          <div className="flex-1 flex flex-col items-center justify-center min-w-0">
            <span className="text-[10px] uppercase tracking-wider text-violet-400/90 font-semibold">
              Yönetim modu
            </span>
            <h1 className="text-xl font-black text-white leading-tight truncate w-full text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              DEKOSTA Admin
            </h1>
          </div>
          <div className="w-10 shrink-0" aria-hidden />
        </div>
        {!isAdminHome && (
          <div className="md:hidden px-4 pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin')}
              className="w-full min-h-[44px] justify-start border-white/20 text-white"
              data-testid="admin-back-to-hub-mobile"
            >
              <ArrowLeftIcon className={`${getIconSize('small')} mr-2`} />
              Yönetim merkezine dön
            </Button>
          </div>
        )}

        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;